import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { api } from '../services/api';

interface Country {
  id: number;
  name: string;
  code: string;
}

interface Board {
  id: number;
  name: string;
  country_id: number;
}

interface Standard {
  id: number;
  name: string;
}

interface Subject {
  id: number;
  name: string;
}

interface Chapter {
  id: number;
  name: string;
}

interface Medium {
  id: number;
  code: string;
  name: string;
}

interface ImportResult {
  success: boolean;
  format_detected?: string;
  total_rows?: number;
  imported?: number;
  failed?: number;
  errors?: Array<{ row: number; error: string }>;
  message?: string;
}

const QuestionImportExport: React.FC = () => {
  // Country and Board selection
  const [countries, setCountries] = useState<Country[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<number | ''>('');
  const [boards, setBoards] = useState<Board[]>([]);
  const [selectedBoard, setSelectedBoard] = useState<number | ''>('');

  // Export filters
  const [mediums, setMediums] = useState<Medium[]>([]);
  const [standards, setStandards] = useState<Standard[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [selectedMedium, setSelectedMedium] = useState<number | ''>('');
  const [selectedStandard, setSelectedStandard] = useState<number | ''>('');
  const [selectedSubject, setSelectedSubject] = useState<number | ''>('');
  const [selectedChapter, setSelectedChapter] = useState<number | ''>('');
  const [exportFormat, setExportFormat] = useState<'A' | 'B'>('B');

  // Import filters
  const [selectedImportMedium, setSelectedImportMedium] = useState<number | ''>('');

  // Import state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load countries on mount
  useEffect(() => {
    loadCountries();
  }, []);

  // Load boards when country changes
  useEffect(() => {
    if (selectedCountry) {
      loadBoards();
      setSelectedBoard('');
      setSelectedStandard('');
      setSelectedSubject('');
      setSelectedChapter('');
    } else {
      setBoards([]);
      setSelectedBoard('');
    }
  }, [selectedCountry]);

  // Load mediums and standards when board changes
  useEffect(() => {
    if (selectedBoard) {
      loadMediums();
      loadStandards();
      setSelectedMedium('');
      setSelectedStandard('');
      setSelectedSubject('');
      setSelectedChapter('');
      setSelectedImportMedium('');
    }
  }, [selectedBoard]);

  // Load subjects when medium or standard changes
  useEffect(() => {
    if (selectedMedium && selectedStandard) {
      loadSubjects();
      setSelectedSubject('');
      setSelectedChapter('');
    } else {
      setSubjects([]);
      setSelectedSubject('');
      setSelectedChapter('');
    }
  }, [selectedMedium, selectedStandard]);

  // Load chapters when subject changes
  useEffect(() => {
    if (selectedSubject) {
      loadChapters();
      setSelectedChapter('');
    }
  }, [selectedSubject]);

  const loadCountries = async () => {
    try {
      const response = await api.master.getCountries(true); // with_boards=true
      setCountries(response.data.countries || []);
    } catch (err: any) {
      console.error('Failed to load countries:', err);
      setError('Failed to load countries');
    }
  };

  const loadBoards = async () => {
    try {
      const response = await api.master.getBoards(selectedCountry as number);
      setBoards(response.data.boards || []);
    } catch (err: any) {
      console.error('Failed to load boards:', err);
      setError('Failed to load boards');
    }
  };

  const loadMediums = async () => {
    try {
      const response = await api.master.getMediums();
      setMediums(response.data.mediums || []);
    } catch (err: any) {
      console.error('Failed to load mediums:', err);
    }
  };

  const loadStandards = async () => {
    try {
      const response = await api.master.getStandards(selectedBoard as number);
      setStandards(response.data.data || []);
    } catch (err: any) {
      console.error('Failed to load standards:', err);
    }
  };

  const loadSubjects = async () => {
    try {
      const response = await api.master.getSubjects(
        selectedBoard as number,
        selectedStandard as number,
        selectedMedium as number
      );
      setSubjects(response.data.data || []);
    } catch (err: any) {
      console.error('Failed to load subjects:', err);
    }
  };

  const loadChapters = async () => {
    try {
      const response = await api.master.getChapters(selectedSubject as number);
      setChapters(response.data.data || []);
    } catch (err: any) {
      console.error('Failed to load chapters:', err);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validExt = ['.tsv', '.csv', '.yml', '.yaml'];
      if (!validExt.some(ext => file.name.toLowerCase().endsWith(ext))) {
        setError('Please select a YAML (.yml/.yaml), TSV, or CSV file');
        return;
      }
      setSelectedFile(file);
      setError('');
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!selectedBoard) {
      setError('Please select a board');
      return;
    }

    if (!selectedImportMedium) {
      setError('Please select a medium');
      return;
    }

    if (!selectedFile) {
      setError('Please select a YAML, TSV, or CSV file');
      return;
    }

    setImportLoading(true);
    setError('');
    setSuccess('');
    setImportResult(null);

    try {
      const response = await api.questions.import(
        selectedBoard as number,
        selectedFile,
        selectedImportMedium as number
      );
      setImportResult(response.data);
      setSuccess('Import completed successfully!');
      setSelectedFile(null);

      // Reset file input
      const fileInput = document.getElementById('csv-file-input') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (err: any) {
      console.error('Import failed:', err);
      setError(err.response?.data?.message || 'Failed to import questions');
      setImportResult(err.response?.data);
    } finally {
      setImportLoading(false);
    }
  };

  const handleExport = async () => {
    if (!selectedBoard) {
      setError('Please select a board');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const params: any = {
        board_id: selectedBoard,
        format: exportFormat,
      };

      if (selectedMedium) params.medium_id = selectedMedium;
      if (selectedStandard) params.standard_id = selectedStandard;
      if (selectedSubject) params.subject_id = selectedSubject;
      if (selectedChapter) params.chapter_id = selectedChapter;

      const response = await api.questions.export(params);

      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/tab-separated-values' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `questions_export_format_${exportFormat}_${Date.now()}.tsv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess('Questions exported successfully!');
    } catch (err: any) {
      console.error('Export failed:', err);
      setError(err.response?.data?.message || 'Failed to export questions');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadSample = async (format: 'A' | 'B') => {
    setLoading(true);
    setError('');

    try {
      const response = await api.questions.downloadSample(format);

      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/tab-separated-values' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `sample_format_${format}.tsv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Download sample failed:', err);
      setError('Failed to download sample CSV');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Question Import/Export
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 3 }}>
        Import questions from TSV files or export existing questions
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Country and Board Selection */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Select Country and Board
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Country Selection */}
          <FormControl fullWidth>
            <InputLabel>Country</InputLabel>
            <Select
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value as number)}
              label="Country"
            >
              <MenuItem value="">
                <em>Select a country</em>
              </MenuItem>
              {countries.map((country) => (
                <MenuItem key={country.id} value={country.id}>
                  {country.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Board Selection */}
          <FormControl fullWidth>
            <InputLabel>Board</InputLabel>
            <Select
              value={selectedBoard}
              onChange={(e) => setSelectedBoard(e.target.value as number)}
              label="Board"
              disabled={!selectedCountry}
            >
              <MenuItem value="">
                <em>Select a board</em>
              </MenuItem>
              {boards.map((board) => (
                <MenuItem key={board.id} value={board.id}>
                  {board.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {/* Export Section */}
        <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DownloadIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Export Questions</Typography>
              </Box>

              <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 2 }}>
                Export questions to TSV file. Apply filters to export specific questions.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                {/* Medium Filter */}
                <FormControl fullWidth size="small">
                  <InputLabel>Medium (Optional)</InputLabel>
                  <Select
                    value={selectedMedium}
                    onChange={(e) => setSelectedMedium(e.target.value as number)}
                    label="Medium (Optional)"
                    disabled={!selectedBoard}
                  >
                    <MenuItem value="">
                      <em>All Mediums</em>
                    </MenuItem>
                    {mediums.map((medium) => (
                      <MenuItem key={medium.id} value={medium.id}>
                        {medium.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Standard Filter */}
                <FormControl fullWidth size="small">
                  <InputLabel>Standard (Optional)</InputLabel>
                  <Select
                    value={selectedStandard}
                    onChange={(e) => setSelectedStandard(e.target.value as number)}
                    label="Standard (Optional)"
                    disabled={!selectedBoard}
                  >
                    <MenuItem value="">
                      <em>All Standards</em>
                    </MenuItem>
                    {standards.map((std) => (
                      <MenuItem key={std.id} value={std.id}>
                        {std.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Subject Filter */}
                <FormControl fullWidth size="small">
                  <InputLabel>Subject (Optional)</InputLabel>
                  <Select
                    value={selectedSubject}
                    onChange={(e) => setSelectedSubject(e.target.value as number)}
                    label="Subject (Optional)"
                    disabled={!selectedMedium || !selectedStandard}
                  >
                    <MenuItem value="">
                      <em>All Subjects</em>
                    </MenuItem>
                    {subjects.map((subject) => (
                      <MenuItem key={subject.id} value={subject.id}>
                        {subject.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Chapter Filter */}
                <FormControl fullWidth size="small">
                  <InputLabel>Chapter (Optional)</InputLabel>
                  <Select
                    value={selectedChapter}
                    onChange={(e) => setSelectedChapter(e.target.value as number)}
                    label="Chapter (Optional)"
                    disabled={!selectedSubject}
                  >
                    <MenuItem value="">
                      <em>All Chapters</em>
                    </MenuItem>
                    {chapters.map((chapter) => (
                      <MenuItem key={chapter.id} value={chapter.id}>
                        {chapter.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Export Format */}
                <FormControl fullWidth size="small">
                  <InputLabel>Export Format</InputLabel>
                  <Select
                    value={exportFormat}
                    onChange={(e) => setExportFormat(e.target.value as 'A' | 'B')}
                    label="Export Format"
                  >
                    <MenuItem value="B">Format B (Recommended - Supports Multiple Choice)</MenuItem>
                    <MenuItem value="A">Format A (Legacy - Single Choice Only)</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Button
                variant="contained"
                fullWidth
                startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
                onClick={handleExport}
                disabled={!selectedBoard || loading}
              >
                Export Questions
              </Button>

              <Divider sx={{ my: 2 }} />

              <Typography variant="body2" color="textSecondary" gutterBottom>
                Download Sample TSV Templates:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FileIcon />}
                  onClick={() => handleDownloadSample('B')}
                  disabled={loading}
                >
                  Format B
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FileIcon />}
                  onClick={() => handleDownloadSample('A')}
                  disabled={loading}
                >
                  Format A
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Import Section */}
        <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <UploadIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Import Questions</Typography>
              </Box>

              <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 2 }}>
                Import questions from TSV file (tab-separated). Format will be auto-detected.
              </Typography>

              {/* Medium Selection for Import */}
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Medium *</InputLabel>
                <Select
                  value={selectedImportMedium}
                  onChange={(e) => setSelectedImportMedium(e.target.value as number)}
                  label="Medium *"
                  disabled={!selectedBoard}
                >
                  <MenuItem value="">
                    <em>Select a medium</em>
                  </MenuItem>
                  {mediums.map((medium) => (
                    <MenuItem key={medium.id} value={medium.id}>
                      {medium.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: selectedFile ? 'primary.main' : 'grey.400',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  mb: 2,
                  cursor: 'pointer',
                  bgcolor: selectedFile ? 'primary.light' : 'transparent',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover',
                  },
                }}
                onClick={() => document.getElementById('csv-file-input')?.click()}
              >
                <input
                  id="csv-file-input"
                  type="file"
                  accept=".yml,.yaml,.tsv,.csv"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
                <FileIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                <Typography variant="body1" gutterBottom>
                  {selectedFile ? selectedFile.name : 'Click to select YAML, TSV, or CSV file'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Tab-separated format. Supports Format A (legacy) and Format B (multiple choice)
                </Typography>
              </Box>

              <Button
                variant="contained"
                fullWidth
                startIcon={importLoading ? <CircularProgress size={20} /> : <UploadIcon />}
                onClick={handleImport}
                disabled={!selectedBoard || !selectedImportMedium || !selectedFile || importLoading}
              >
                Import Questions
              </Button>

              {importLoading && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress />
                  <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                    Importing questions...
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Import Results */}
      {importResult && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Import Results
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <Box sx={{ flex: '1 1 20%', minWidth: '150px' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {importResult.total_rows || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Rows
                </Typography>
              </Box>
            </Box>
            <Box sx={{ flex: '1 1 20%', minWidth: '150px' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="success.main">
                  {importResult.imported || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Imported
                </Typography>
              </Box>
            </Box>
            <Box sx={{ flex: '1 1 20%', minWidth: '150px' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="error.main">
                  {importResult.failed || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Failed
                </Typography>
              </Box>
            </Box>
            <Box sx={{ flex: '1 1 20%', minWidth: '150px' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Chip
                  label={importResult.format_detected || 'Unknown'}
                  color="primary"
                  sx={{ fontSize: '1rem', px: 2, py: 2 }}
                />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Format Detected
                </Typography>
              </Box>
            </Box>
          </Box>

          {importResult.errors && importResult.errors.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom color="error">
                Errors ({importResult.errors.length})
              </Typography>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importResult.errors.map((error, index) => (
                      <TableRow key={index}>
                        <TableCell>{error.row}</TableCell>
                        <TableCell>{error.error}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default QuestionImportExport;
